import { db } from "../../lib/api";
import { invalidate, useQuery } from "../../lib/hooks";
import { PageHead } from "../../components/Shell";
import { Empty, ErrorBox, Loading, Panel, fmtDate, useToast } from "../../components/ui";

type R = { id: string; full_name: string; email: string | null; phone: string | null; is_client: boolean; removed_at: string;
  removed_by: string | null; reason: string | null; merged_into: string | null; merged_into_name: string | null; erase_on: string };

/** The 30-day bin for removed and merged people. */
export default function Removed() {
  const toast = useToast();
  const q = useQuery("removed", () => db.rpc<R[]>("removed_people"), { staleMs: 0 });
  async function restore(r: R) {
    const res = await db.rpc<{ ok: boolean; reason?: string }>("person_restore", { p_person: r.id });
    if (!res.ok) { toast(res.reason ?? "Couldn't restore"); return; }
    invalidate("removed"); invalidate("people"); invalidate("person:"); toast(`${r.full_name} is back`);
  }
  return (
    <>
      <PageHead title="Removed" sub="People removed or merged, and deals deleted, in the last 30 days. After 30 days they're erased for good." />
      <ErrorBox error={q.error} />
      <Panel>
        {q.loading && !q.data ? <Loading /> : !q.data?.length ? <Empty title="Nothing here">Removed and merged people show up here for 30 days.</Empty> : (
          <div className="table-wrap"><table className="data">
            <thead><tr><th>Name</th><th>What happened</th><th>By</th><th>Erased on</th><th /></tr></thead>
            <tbody>{q.data.map((r) => (
              <tr key={r.id}>
                <td><b>{r.full_name || "(no name)"}</b><div className="small muted">{[r.phone, r.email].filter(Boolean).join(" · ")}</div></td>
                <td>{r.merged_into ? <>Merged into <b>{r.merged_into_name ?? "another record"}</b></> : <>Removed{r.reason ? `: ${r.reason}` : ""}</>}
                  <div className="small muted">{fmtDate(r.removed_at, "MMM D, YYYY h:mm A")}</div></td>
                <td>{r.removed_by ?? "—"}</td>
                <td>{fmtDate(r.erase_on)}</td>
                <td className="num">{r.merged_into ? <span className="small muted">History is on the kept record</span> : <button className="btn small" onClick={() => restore(r)}>Restore</button>}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Panel>
      <RemovedDeals />
    </>
  );
}

type D = { id: string; person_id: string; full_name: string | null; board: string; stage: string; outcome: string; removed_at: string; removed_by: string | null; reason: string | null; erase_on: string };

/** Deleted pipeline deals, kept 30 days. */
function RemovedDeals() {
  const toast = useToast();
  const q = useQuery("removed:deals", () => db.rpc<D[]>("removed_deals"), { staleMs: 0 });
  async function restore(d: D) {
    const res = await db.rpc<{ ok: boolean; reason?: string }>("deal_restore", { p_id: d.id });
    if (!res.ok) { toast(res.reason ?? "Couldn't restore"); return; }
    invalidate("removed"); invalidate("pipeline"); invalidate(`person:${d.person_id}`); invalidate("cc"); toast("Deal is back in the pipeline");
  }
  return (
    <Panel title="Deleted deals" className="mt16">
      {q.loading && !q.data ? <Loading /> : !q.data?.length ? <Empty title="No deleted deals">Deals you delete show up here for 30 days.</Empty> : (
        <div className="table-wrap"><table className="data">
          <thead><tr><th>Deal with</th><th>Where it was</th><th>Deleted</th><th>Erased on</th><th /></tr></thead>
          <tbody>{q.data.map((d) => (
            <tr key={d.id}>
              <td><b>{d.full_name ?? "(removed person)"}</b></td>
              <td>{d.board} · {d.stage}{d.outcome !== "Open" ? ` · ${d.outcome}` : ""}</td>
              <td>{fmtDate(d.removed_at, "MMM D, YYYY h:mm A")}<div className="small muted">{d.removed_by ?? ""}{d.reason ? ` · ${d.reason}` : ""}</div></td>
              <td>{fmtDate(d.erase_on)}</td>
              <td className="num"><button className="btn small" onClick={() => restore(d)}>Restore</button></td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </Panel>
  );
}
